import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { LockNotice } from "@/components/LockNotice";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockNotice, setLockNotice] = useState("");
  const { setSession } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLockNotice("");
    try {
      const res = await api.post("/auth/login", { email, password });
      setSession(res.data.token, res.data.user);
      navigate("/app/log");
    } catch (err) {
      if (err?.response?.status === 429) {
        setLockNotice(apiError(err, "Too many attempts. Please try again in a little while."));
      } else {
        toast.error(apiError(err, "Login failed."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.welcomeBack")} subtitle={t("auth.loginSubtitle")}>
      {lockNotice && (
        <LockNotice message={lockNotice} ctaTo="/forgot-password" ctaLabel={t("auth.resetPasswordCta")} ctaState={{ email }} />
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="mt-1.5" data-testid="login-email" placeholder="you@work.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Link to="/forgot-password" state={{ email }} className="text-xs text-[#202123] hover:underline" data-testid="forgot-link">{t("auth.forgot")}</Link>
          </div>
          <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                 className="mt-1.5" data-testid="login-password" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="login-submit">
          {loading ? t("auth.loggingIn") : t("auth.login")}
        </Button>
      </form>
      <p className="text-sm text-slatebody mt-6 text-center">
        {t("auth.noAccount")} <Link to="/signup" className="text-[#202123] font-medium hover:underline" data-testid="to-signup">{t("auth.startFree")}</Link>
      </p>
    </AuthShell>
  );
}
